import calendar from '@iconify/icons-carbon/calendar';
import { Box, Button, Container, Grid, Typography } from '@mui/material';
import Image from 'next/image';

import { Consumer, Provider, Section } from '@larvitu/shared/mui/ui-components';
import { Iconify } from '@larvitu/shared/mui/ui-iconify';
import { Link } from '@larvitu/shared/next/mui';
import { For } from '@larvitu/shared/react/components/feature-for';

import praxisGespraech from '../../../public/assets/images/geiges/859_Dr_Geiges_025.jpg';
import { SectionStyle } from '../SectionStyle';
import { ThemedBackground } from '../themed-background';

import { ServiceCard } from './ServiceCard';

import type { foreach } from '@larvitu/shared/mui/ui-components';
import type { Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import type { FC } from 'react';

interface ServiceComponentProps {
  header: string;
  content: string;
  serviceItems: Array<{ id: string }>;
  contactButton: string;
}

export const ServiceComponent: FC<ServiceComponentProps> = ({
  header,
  content,
  serviceItems,
  contactButton,
}) => {
  const containedButtonHover = {
    backgroundColor: 'rgba(24, 84, 77, 0.1)',
    border: '1px solid #174741',
    color: 'rgba(24, 84, 77, 1)',
  };

  return (
    <ThemedBackground>
      <SectionStyle>
        <Container>
          <Typography my={2} fontWeight="bold" gutterBottom variant="h2">
            {header}
          </Typography>
          <Typography my={2} maxWidth="80%" paragraph gutterBottom>
            {content}
          </Typography>

          <Grid container direction="row" spacing={3} my={2}>
            <For each={serviceItems}>
              <Provider>
                <Grid item xs={12} md={4}>
                  <Provider.Consumer>
                    <ServiceCard />
                  </Provider.Consumer>
                </Grid>
              </Provider>
            </For>
          </Grid>
          <Box my={5}>
            <Button
              component={Link}
              href="/contact"
              size="large"
              sx={{ minWidth: 240, '&:hover': containedButtonHover }}
              variant="contained"
              endIcon={<Iconify icon={calendar} />}
            >
              {contactButton}
            </Button>
          </Box>
        </Container>
      </SectionStyle>
    </ThemedBackground>
  );
};

// -----------------------------------------------------------------------------
// *****************************************************************************
// -----------------------------------------------------------------------------

// service card creation
interface SubServiceCardProps {
  id?: string;
  content?: string;
  icon?: { src: string; alt: string; title: string };
  title?: string;
  children?: SubServiceCardProps[];
}

const defaultIcon = { src: '', alt: '', title: '' };

export const SubServiceCard: FC<
  foreach.Iterable<SubServiceCardProps> & {
    contactButton: string;
  }
> = ({
  contactButton,
  data,
  // data: {
  //   icon = defaultIcon,
  //   content = '',
  //   children = null,
  //   title = '',
  // }
}) => {
  const containedButtonHover = {
    backgroundColor: 'rgba(24, 84, 77, 0.1)',
    border: '1px solid #174741',
    color: 'rgba(24, 84, 77, 1)',
  };

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition, @typescript-eslint/strict-boolean-expressions
  if (data?.children) {
    return (
      <>
        <Grid item xs={12}>
          <Button
            component={Link}
            href="/contact"
            size="large"
            sx={{ minWidth: 240, '&:hover': containedButtonHover }}
            variant="contained"
            endIcon={<Iconify icon={calendar} />}
          >
            {contactButton}
          </Button>
        </Grid>
        <Grid item xs={12} display="flex" flexDirection="column">
          <Grid container justifyContent="center" spacing={3} rowSpacing={6}>
            <Grid item xs={12}>
              <Typography variant="h3">
                {data.title}
              </Typography>
            </Grid>
            <For each={data.children}>
              <SubServiceCard contactButton={contactButton} />
            </For>
          </Grid>
        </Grid>
      </>
    );
  }

  return (
    <Grid item xs={12} sm={6} lg={4} display="flex">
      <Box
        display="flex"
        alignItems="center"
        gap={2}
        p={2}
        mx="auto"
        boxSizing="content-box"
        width="100%"
        sx={{
          borderWidth: 1,
          borderColor: 'rgba(200, 200, 200, 0.2)',
          borderStyle: 'solid',
          boxShadow: 8,
        }}
      >
        <Box position="relative" minWidth={32} minHeight={32}>
          <Image
            src={data?.icon?.src ?? ''}
            alt={data?.icon?.alt ?? ''}
            title={data?.icon?.title ?? ''}
            fill
          />
        </Box>
        <Typography color="text.secondary" mb={0} paragraph>
          {data?.content}
        </Typography>
      </Box>
    </Grid>
  );
};

interface Services {
  id: string;
  query: string;
  title: string;
  content: string;
  subContent?: string;
  icon: { src: string; alt: string };
  items: Array<{ id: string }>;
}

interface SubServiceComponentProps {
  services: Services[];
  query: string;
  contactButton: string;
  subHeader: string;
  readMore: string;
}

export const SubServiceComponent: FC<SubServiceComponentProps> = ({
  services,
  query,
  contactButton,
  subHeader,
  readMore,
}) => {
  const containedButtonHover = {
    backgroundColor: 'rgba(24, 84, 77, 0.1)',
    border: '1px solid #174741',
    color: 'rgba(24, 84, 77, 1)',
  };
  const remainingServices: Services[] = services.filter(
    (item) => item.query !== query,
  );
  const thisServiceArray: Services[] = services.filter(
    (item) => item.query === query,
  );
  const [thisService] = thisServiceArray;

  return (
    <>
      <Section id="description">
        <SectionStyle>
          <Container
            /* maxWidth={"lg"} */
            sx={{
              zIndex: 1,
              position: 'relative',
              // marginTop: { xs: 15, md: 25, lg: 30 },
            }}
          >
            <Box display="flex" alignItems="center">
              <Box
                mr={{ xs: 1, md: 2, lg: 3 }}
                display="flex"
                height={{ xs: 32, md: 40, lg: 48 }}
                width={{ xs: 32, md: 48 }}
                position="relative"
              >
                <Image
                  src={thisService.icon.src}
                  alt={thisService.icon.alt}
                  layout="fill"
                />
              </Box>
              <Typography textAlign="start" fontWeight="bold" variant="h2">
                {thisService.title}
              </Typography>
            </Box>
            <Typography mt={5} textAlign="start" maxWidth={600}>
              {thisService.content}
            </Typography>

            <Grid container justifyContent="center" spacing={3} mt={2}>
              <For each={thisService.items}>
                <SubServiceCard contactButton={contactButton} />
              </For>

              <Grid item xs={12}>
                <Button
                  component={Link}
                  href="/contact"
                  size="large"
                  sx={{ minWidth: 240, '&:hover': containedButtonHover }}
                  variant="contained"
                  endIcon={<Iconify icon={calendar} />}
                >
                  {contactButton}
                </Button>
              </Grid>
            </Grid>
          </Container>
        </SectionStyle>
      </Section>

      {/* eslint-disable-next-line @typescript-eslint/strict-boolean-expressions */}
      {thisService.subContent
        ? (
          <Section id="subContent">
            <Container>
              <Typography>
                {thisService.subContent}
              </Typography>
            </Container>
          </Section>
        )
        : null}

      <Section id="otherServices">
        <SectionStyle>
          <Container>
            <Typography my={4} fontWeight="bold" gutterBottom variant="h2">
              {subHeader}
            </Typography>
            <Box
              display="flex"
              flexDirection={{ xs: 'column', lg: 'row' }}
              gap={{ xs: 6, lg: 0 }}
              justifyContent={{ md: 'space-between' }}
            >
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignSelf="center"
                width={{ xs: '100%', lg: '55%' }}
                maxWidth={{ sm: 600 }}
              >
                <Image
                  width={600}
                  src={praxisGespraech}
                  alt="besprechung"
                  title="besprechung"
                  placeholder="blur"
                />
              </Box>
              <Grid container width={{ lg: '45%' }} spacing={3}>
                <For each={remainingServices}>
                  <Provider>
                    <Grid item xs={12}>
                      <Consumer>
                        <ServiceCard
                          // readMore={readMore}
                          sx={(theme: Theme): SystemStyleObject<Theme> => ({
                            width: '100%',
                            height: '100%',
                            background: theme.palette.background.default,
                            boxShadow: 3,
                            transition: 'all 200ms ease-in-out',
                            '&:hover': {
                              boxShadow: 20,
                              textDecoration: 'none',
                            },
                          })}
                        />
                      </Consumer>
                    </Grid>
                  </Provider>
                </For>
              </Grid>
            </Box>
          </Container>
        </SectionStyle>
      </Section>
    </>
  );
};
